import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Select, DatePicker, Button, Input, Tooltip, message } from 'antd';
import { WarningOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import { saveDateRange, loadDateRange, formatDateForRangePicker } from 'utils/datePersistence';
import { isWarehousingProject } from 'componentsv2/forms/project/projectTypes';
import BulkTripFetchModal from 'componentsv2/common/BulkTripFetchModal';
import UnfetchedTripsPill from 'componentsv2/common/UnfetchedTrips';
import MultiSelectDropdown, { ALL_VALUE } from './MultiSelectDropdown';

const { Option } = Select;
export const SHIPPING_METHOD_OPTIONS = [
	{ value: 'road', label: 'Road' },
	{ value: 'rail', label: 'Rail' },
	{ value: 'water', label: 'Water' },
	{ value: 'warehouse', label: 'Warehouse' },
];
export const TRIP_TYPE_OPTIONS = [
	{ value: 'intercity_adhoc', label: 'Intercity Ad-Hoc' },
	{ value: 'intracity_adhoc', label: 'Intracity Ad-Hoc' },
	{ value: 'intracity_regular', label: 'Intracity Regular' },
];

const Card = styled(Box)`
	background: #fff;
	border-radius: 8px;
	padding: 28px 32px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px 32px;
`;

const FieldWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const FieldLabel = styled.label`
	font-size: 14px;
	font-weight: 500;
	color: #374151;
`;

const Required = styled.span`
	color: #ef4444;
	margin-left: 2px;
`;

const PrimaryButton = styled(Button)`
	height: 40px;
	padding: 0 28px;
	background-color: #1e3a5f;
	border-color: #1e3a5f;
	color: #fff;
	border-radius: 6px;
	font-weight: 500;

	&:hover,
	&:focus {
		background-color: #18405d !important;
		border-color: #18405d !important;
		color: #fff !important;
	}

	&:disabled {
		background-color: #9ca3af !important;
		border-color: #9ca3af !important;
		color: #fff !important;
	}
`;
const SecondaryButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background-color: #fff;
	border-color: #1e3a5f;
	color: #1e3a5f;
	border-radius: 6px;
	font-weight: 500;

	&:hover,
	&:focus {
		background-color: #f1f5f9 !important;
		border-color: #18405d !important;
		color: #18405d !important;
	}

	&:disabled {
		background-color: #fff !important;
		border-color: #d1d5db !important;
		color: #9ca3af !important;
	}
`;

const WarningBanner = styled.div`
	margin-top: 20px;
	background: #fffbeb;
	border: 1px solid #fde68a;
	border-radius: 6px;
	padding: 12px 16px;
	color: #b45309;
	display: flex;
	align-items: center;
	gap: 10px;
	font-size: 14px;
`;

const TripSearchInput = styled(Input)`
	width: 320px;
	border-radius: 6px;
`;

const HintText = styled.div`
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
`;

const TRIP_SEARCH_DEBOUNCE_MS = 500;

const formatINR = (value) => {
	if (value == null || value === '') return '-';
	const num = Number(value);
	return Number.isNaN(num) ? value : `₹ ${num.toLocaleString('en-IN')}`;
};

const TRIP_COLUMNS = [
	{
		title: 'Trip ID',
		dataIndex: 'display_id',
		key: 'display_id',
		render: (t, record) => <b>{t || record.trip_code || record.trip_id || '-'}</b>,
	},
	{
		title: 'Date',
		dataIndex: 'date',
		key: 'date',
		render: (t) => (t ? moment(t).format('DD/MM/YYYY') : '-'),
	},
	{
		title: 'Source',
		dataIndex: 'origin',
		key: 'origin',
		width: 220,
		render: (t) => (
			<div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t || '-'}</div>
		),
	},
	{
		title: 'Destination',
		dataIndex: 'destination',
		key: 'destination',
		width: 220,
		render: (t) => (
			<div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t || '-'}</div>
		),
	},
	{
		title: 'Vehicle No',
		dataIndex: 'vehicle_number',
		key: 'vehicle_number',
		render: (t) => t || '-',
	},
	{
		title: 'Vehicle Type',
		dataIndex: 'vehicle_type',
		key: 'vehicle_type',
		render: (t) => t || '-',
	},
	{ title: 'Driver', dataIndex: 'driver', key: 'driver', render: (t) => t || '-' },
	{ title: 'Trip Type', dataIndex: 'trip_type', key: 'trip_type', render: (t) => t || '-' },
	{
		title: 'Distance(km)',
		dataIndex: 'distance_km',
		key: 'distance_km',
		render: (t) => t ?? '-',
	},
	{ title: 'Base Fare', dataIndex: 'base_fare', key: 'base_fare', render: formatINR },
	{ title: 'Toll/Extra', dataIndex: 'toll_extra', key: 'toll_extra', render: formatINR },
	{ title: 'Total', dataIndex: 'total', key: 'total', render: formatINR },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
function TripSelectionStep({
	clients,
	projects,
	vehicleTypes,
	vehicleNumbers,
	onFetchTrips,
	onBulkFetchTrips,
	onProceed,
	onSearchVehicleNumbers,
	vehicleSearchLoading,
}) {
	const storedDateRange = loadDateRange('CLIENT_INVOICE');
	const [filters, setFilters] = useState({
		client_id: undefined,
		// Multi-select: one client's projects can be billed on a single invoice
		// (nearby sites that share vehicles), so the whole selection travels on.
		project_ids: [],
		vehicle_types: [],
		vehicle_numbers: [],
		shipping_methods: [],
		trip_types: [],
		from_date: formatDateForRangePicker(storedDateRange?.from_date_moment),
		to_date: formatDateForRangePicker(storedDateRange?.to_date_moment),
	});
	const [trips, setTrips] = useState([]);
	const [fetched, setFetched] = useState(false);
	const [loading, setLoading] = useState(false);
	const [bulkOpen, setBulkOpen] = useState(false);
	const [bulkResult, setBulkResult] = useState(null);
	const [bulkMode, setBulkMode] = useState(false);
	const [tripSearch, setTripSearch] = useState('');
	const [appliedTripSearch, setAppliedTripSearch] = useState('');
	const [tripSearchLoading, setTripSearchLoading] = useState(false);
	const [tablePage, setTablePage] = useState(1);
	const [tablePageSize, setTablePageSize] = useState(10);
	const [selectedTrips, setSelectedTrips] = useState([]);
	// The project selection the rows currently on screen were fetched with — not the
	// live one. Editing the Project field must not restyle a table it has not
	// re-queried yet.
	const [fetchedProjectIds, setFetchedProjectIds] = useState([]);
	const selectedRowKeys = useMemo(() => selectedTrips.map((t) => t.trip_id), [selectedTrips]);
	const isWarehouseFlow =
		filters.shipping_methods.length === 1 && filters.shipping_methods[0] === 'warehouse';

	const setField = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

	// A warehousing project bills warehousing, never a transport lane, so picking one
	// sets the shipping method for the user. Moving back to a transportation project
	// clears it rather than leaving the warehouse flow (which skips the trip fetch) on.
	const isWarehousingProjectId = (id) =>
		isWarehousingProject(projects.find((p) => p.value === id)?.project_type);
	const hasWarehousingProject = (ids = []) => ids.some(isWarehousingProjectId);

	const resolveShippingMethods = (prev, nextProjectIds) => {
		if (hasWarehousingProject(nextProjectIds)) return ['warehouse'];
		if (hasWarehousingProject(prev.project_ids)) return [];
		return prev.shipping_methods;
	};

	const handleClientChange = (v) =>
		setFilters((prev) => ({
			...prev,
			client_id: v,
			project_ids: [],
			shipping_methods: resolveShippingMethods(prev, []),
		}));

	// Warehousing is priced per project from the Warehousing module, so it can never
	// share an invoice (the backend rejects the mix; this stops the user getting
	// there). Deliberately picking one warehousing project collapses the selection to
	// it; any broader selection — including the dropdown's Select All — simply drops
	// the warehousing projects and keeps the transport ones.
	const handleProjectChange = (values = []) =>
		setFilters((prev) => {
			const added = values.filter((v) => !prev.project_ids.includes(v));
			const pickedOneWarehousing = added.length === 1 && isWarehousingProjectId(added[0]);
			let next = values;
			if (pickedOneWarehousing) {
				next = [added[0]];
			} else if (hasWarehousingProject(values)) {
				next = values.filter((v) => !isWarehousingProjectId(v));
			}
			if (next.length !== values.length) {
				message.info('A warehousing project is billed on its own invoice.');
			}
			return {
				...prev,
				project_ids: next,
				shipping_methods: resolveShippingMethods(prev, next),
			};
		});

	const visibleProjects = filters.client_id
		? projects.filter((p) => p.client_id === filters.client_id)
		: projects;

	const handleVehicleTypesChange = (v) =>
		setFilters((prev) => ({ ...prev, vehicle_types: v, vehicle_numbers: [] }));
	const typeAllSelected = filters.vehicle_types.includes(ALL_VALUE);
	let visibleVehicleNumbers = [];
	if (typeAllSelected) {
		visibleVehicleNumbers = vehicleNumbers;
	} else if (filters.vehicle_types.length) {
		visibleVehicleNumbers = vehicleNumbers.filter((n) =>
			filters.vehicle_types.includes(n.vehicle_type_id)
		);
	}
	const searchTimer = useRef(null);
	const handleVehicleSearch = (term) => {
		if (!onSearchVehicleNumbers) return;
		if (searchTimer.current) clearTimeout(searchTimer.current);
		const q = (term || '').trim();
		if (q.length < 2) return;
		searchTimer.current = setTimeout(() => onSearchVehicleNumbers(q), 400);
	};

	const tripSearchTimer = useRef(null);
	const tripFetchSeq = useRef(0);
	useEffect(
		() => () => {
			clearTimeout(searchTimer.current);
			clearTimeout(tripSearchTimer.current);
		},
		[]
	);

	const disableFutureDates = (current) => current && current > moment().endOf('day');

	const handleFetchTrips = async () => {
		if (!filters.client_id) {
			message.warning('Please select a Client.');
			return;
		}
		if (!filters.project_ids.length) {
			message.warning('Please select at least one Project.');
			return;
		}
		if (!filters.vehicle_types.length) {
			message.warning('Please select at least one Vehicle Type.');
			return;
		}
		if (!filters.vehicle_numbers.length) {
			message.warning('Please select at least one Vehicle Number.');
			return;
		}
		if (!filters.shipping_methods.length) {
			message.warning('Please select a Shipping Method.');
			return;
		}
		if (!filters.from_date || !filters.to_date) {
			message.warning('Please select From Date and To Date.');
			return;
		}
		clearTimeout(tripSearchTimer.current);
		tripFetchSeq.current += 1;
		const seq = tripFetchSeq.current;
		setLoading(true);
		try {
			const rows = await onFetchTrips(filters);
			if (seq !== tripFetchSeq.current) return;
			setTrips(rows || []);
			setFetchedProjectIds(filters.project_ids);
			setFetched(true);
			// New filters: previous selections and any bulk-fetch summary no
			// longer describe what is on screen.
			setSelectedTrips([]);
			setBulkResult(null);
			setBulkMode(false);
			setTripSearch('');
			setAppliedTripSearch('');
			setTablePage(1);
			saveDateRange('CLIENT_INVOICE', {
				from_date: filters.from_date.format('DD-MM-YYYY'),
				to_date: filters.to_date.format('DD-MM-YYYY'),
				from_date_moment: filters.from_date,
				to_date_moment: filters.to_date,
			});
		} finally {
			setLoading(false);
		}
	};

	const canBulkFetch = Boolean(filters.client_id && filters.project_ids.length);
	const handleBulkFetch = async (file) => {
		clearTimeout(tripSearchTimer.current);
		tripFetchSeq.current += 1;
		const seq = tripFetchSeq.current;
		setLoading(true);
		try {
			const { rows = [], bulkFetch = null } = await onBulkFetchTrips(filters, file);
			if (seq !== tripFetchSeq.current) return;
			setTrips(rows);
			setFetchedProjectIds(filters.project_ids);
			setSelectedTrips(rows);
			setBulkResult(bulkFetch);
			setBulkMode(true);
			setFetched(true);
			if (rows.length && !filters.shipping_methods.length) {
				setField('shipping_methods', ['road']);
			}
			setTripSearch('');
			setAppliedTripSearch('');
			setTablePage(1);
			if (!rows.length) {
				message.warning('No trips could be fetched from the uploaded file.');
			}
		} finally {
			if (seq === tripFetchSeq.current) setLoading(false);
		}
	};

	// Trip ID search re-queries the backend with the same filters plus `trip_search`
	// (ilike over display_id / trip_code / id). Selections are held separately, so a
	// narrower result set never drops what the user already picked.
	const applyTripSearch = async (term) => {
		const trimmed = (term || '').trim();
		tripFetchSeq.current += 1;
		const seq = tripFetchSeq.current;
		setTripSearchLoading(true);
		try {
			const rows = await onFetchTrips(filters, { tripSearch: trimmed, isSearch: true });
			if (seq !== tripFetchSeq.current) return;
			setTrips(rows || []);
			// This re-queries with the live filters, so the column follows the results.
			setFetchedProjectIds(filters.project_ids);
			setAppliedTripSearch(trimmed);
			setTablePage(1);
		} finally {
			if (seq === tripFetchSeq.current) setTripSearchLoading(false);
		}
	};

	const handleTripSearchChange = (value) => {
		setTripSearch(value);
		clearTimeout(tripSearchTimer.current);
		// Clearing restores the full list right away — no need to wait out the debounce.
		if (!(value || '').trim()) {
			if (appliedTripSearch) applyTripSearch('');
			return;
		}
		tripSearchTimer.current = setTimeout(() => applyTripSearch(value), TRIP_SEARCH_DEBOUNCE_MS);
	};

	const handleTripSearchSubmit = () => {
		clearTimeout(tripSearchTimer.current);
		applyTripSearch(tripSearch);
	};

	// With one project every row would repeat the same name, so the column only
	// earns its width when the fetch spanned several. Keyed on what was fetched, so
	// editing the Project field leaves the current table alone until it is re-fetched.
	const tripColumns = useMemo(() => {
		if (fetchedProjectIds.length < 2) return TRIP_COLUMNS;
		const [tripId, ...rest] = TRIP_COLUMNS;
		return [
			tripId,
			{
				title: 'Project',
				dataIndex: 'project',
				key: 'project',
				render: (t) => t || '-',
			},
			...rest,
		];
	}, [fetchedProjectIds]);

	const visibleTripIds = useMemo(() => new Set(trips.map((t) => t.trip_id)), [trips]);
	const hiddenSelectedCount = useMemo(
		() => selectedTrips.filter((t) => !visibleTripIds.has(t.trip_id)).length,
		[selectedTrips, visibleTripIds]
	);

	// Keep the already-picked rows (including any the current search hides) and append
	// whatever was just ticked on the visible page.
	const handleSelectionChange = (keys) => {
		const keySet = new Set(keys);
		const kept = selectedTrips.filter((t) => keySet.has(t.trip_id));
		const keptIds = new Set(kept.map((t) => t.trip_id));
		const added = trips.filter((t) => keySet.has(t.trip_id) && !keptIds.has(t.trip_id));
		setSelectedTrips([...kept, ...added]);
	};

	const handleProceed = () => {
		if (isWarehouseFlow) {
			onProceed({ filters, selectedTrips: [], isWarehouseFlow: true });
			return;
		}
		if (!selectedTrips.length) {
			message.warning('Please select at least one trip.');
			return;
		}
		if (!filters.shipping_methods.length) {
			message.warning('Please select a Shipping Method before proceeding.');
			return;
		}
		onProceed({ filters, selectedTrips, isWarehouseFlow: false });
	};

	return (
		<Box>
			<Card>
				<Grid>
					<FieldWrapper>
						<FieldLabel>
							Client<Required>*</Required>
						</FieldLabel>
						<Select
							placeholder="Select"
							showSearch
							optionFilterProp="children"
							value={filters.client_id}
							onChange={handleClientChange}
							allowClear
						>
							{clients.map((c) => (
								<Option key={c.value} value={c.value}>
									{c.label}
								</Option>
							))}
						</Select>
					</FieldWrapper>

					<MultiSelectDropdown
						label="Project"
						required
						options={visibleProjects}
						value={filters.project_ids}
						onChange={handleProjectChange}
						disabled={!filters.client_id}
						placeholder={filters.client_id ? 'Select' : 'Select a client first'}
					/>

					<MultiSelectDropdown
						label="Vehicle Type"
						required={!bulkMode}
						allSentinel
						options={vehicleTypes}
						value={filters.vehicle_types}
						onChange={handleVehicleTypesChange}
					/>

					<MultiSelectDropdown
						label="Vehicle Numbers"
						required={!bulkMode}
						allSentinel
						options={visibleVehicleNumbers}
						value={filters.vehicle_numbers}
						onChange={(v) => setField('vehicle_numbers', v)}
						onSearch={typeAllSelected ? handleVehicleSearch : undefined}
						searchLoading={vehicleSearchLoading}
						disabled={!filters.vehicle_types.length}
						placeholder={
							filters.vehicle_types.length ? 'Select' : 'Select a vehicle type first'
						}
					/>

					<FieldWrapper>
						<FieldLabel>
							Shipping Method<Required>*</Required>
						</FieldLabel>
						<Select
							placeholder="Select"
							showSearch
							optionFilterProp="children"
							value={filters.shipping_methods[0]}
							onChange={(v) => setField('shipping_methods', v ? [v] : [])}
							allowClear
						>
							{SHIPPING_METHOD_OPTIONS.map((o) => (
								<Option key={o.value} value={o.value}>
									{o.label}
								</Option>
							))}
						</Select>
						{bulkMode && (
							<HintText>
								Set to Road so the invoice can be generated — it picks the SAC code
								on every line. Change it if these trips bill differently.
							</HintText>
						)}
					</FieldWrapper>

					<FieldWrapper>
						<FieldLabel>Trip Type</FieldLabel>
						<Select
							mode="multiple"
							placeholder="Select"
							showSearch
							optionFilterProp="children"
							value={filters.trip_types}
							onChange={(v) => setField('trip_types', v)}
							allowClear
						>
							{TRIP_TYPE_OPTIONS.map((o) => (
								<Option key={o.value} value={o.value}>
									{o.label}
								</Option>
							))}
						</Select>
					</FieldWrapper>

					<Grid>
						<FieldWrapper>
							<FieldLabel>
								From Date{bulkMode ? null : <Required>*</Required>}
							</FieldLabel>
							<DatePicker
								style={{ width: '100%' }}
								format="DD-MM-YYYY"
								placeholder="Choose Date"
								value={filters.from_date}
								disabledDate={disableFutureDates}
								onChange={(d) => setField('from_date', d)}
							/>
						</FieldWrapper>
						<FieldWrapper>
							<FieldLabel>
								To Date{bulkMode ? null : <Required>*</Required>}
							</FieldLabel>
							<DatePicker
								style={{ width: '100%' }}
								format="DD-MM-YYYY"
								placeholder="Choose Date"
								value={filters.to_date}
								disabledDate={disableFutureDates}
								onChange={(d) => setField('to_date', d)}
							/>
						</FieldWrapper>
					</Grid>

					{bulkMode && (
						<HintText style={{ gridColumn: '1 / -1', marginTop: -8 }}>
							Vehicle Type, Vehicle Numbers and the date range are not used when trips
							come from a file — the uploaded Trip IDs decide what is fetched. Dates
							you set here are still recorded as the invoice’s billing period.
						</HintText>
					)}
				</Grid>

				{isWarehouseFlow ? (
					<>
						<WarningBanner>
							<WarningOutlined />
							Warehouse Selected — no trip fetch required. Proceed to Invoice Preview.
						</WarningBanner>
						<Flex justifyContent="center" mt={3}>
							<PrimaryButton onClick={handleProceed}>
								Proceed to Invoice Generation
							</PrimaryButton>
						</Flex>
					</>
				) : (
					<Flex justifyContent="center" mt={3} style={{ gap: 12 }}>
						<PrimaryButton loading={loading} onClick={handleFetchTrips}>
							Fetch Trips
						</PrimaryButton>
						<Tooltip
							title={
								canBulkFetch
									? ''
									: 'Select Client and at least one Project to enable bulk fetch'
							}
						>
							<span>
								<SecondaryButton
									icon={<UploadOutlined />}
									disabled={!canBulkFetch || loading}
									onClick={() => setBulkOpen(true)}
								>
									Bulk Fetch
								</SecondaryButton>
							</span>
						</Tooltip>
					</Flex>
				)}
			</Card>

			{/* Hidden in the warehouse flow: it skips trip fetching entirely. */}
			{!isWarehouseFlow && (
				<BulkTripFetchModal
					open={bulkOpen}
					onCancel={() => setBulkOpen(false)}
					onUpload={handleBulkFetch}
					contextLabel="the selected client and projects"
				/>
			)}

			{!isWarehouseFlow && fetched && (
				<Card mt={3}>
					<Flex
						justifyContent="space-between"
						alignItems="center"
						flexWrap="wrap"
						style={{ gap: 12, marginBottom: 12 }}
					>
						<Box>
							<Flex alignItems="center" style={{ gap: 10 }}>
								<Text style={{ fontSize: 16, fontWeight: 600 }}>
									Trips List ({trips.length})
								</Text>
								<UnfetchedTripsPill bulkResult={bulkResult} context="client" />
							</Flex>
							{appliedTripSearch && (
								<HintText>Filtered by Trip ID “{appliedTripSearch}”</HintText>
							)}
						</Box>
						<Box>
							<TripSearchInput
								allowClear
								prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
								placeholder="Search by Trip ID"
								value={tripSearch}
								onChange={(e) => handleTripSearchChange(e.target.value)}
								onPressEnter={handleTripSearchSubmit}
							/>
						</Box>
					</Flex>
					<AntdTable
						rowKey="trip_id"
						columns={tripColumns}
						dataSource={trips}
						loading={loading || tripSearchLoading}
						pagination={{
							current: tablePage,
							pageSize: tablePageSize,
							showSizeChanger: true,
							pageSizeOptions: ['10', '20', '50', '100'],
							showTotal: (total) => `Total ${total} trips`,
							onChange: (page, size) => {
								setTablePage(page);
								setTablePageSize(size);
							},
						}}
						scroll={{ x: 'max-content' }}
						rowSelection={{
							selectedRowKeys,
							onChange: handleSelectionChange,
							preserveSelectedRowKeys: true,
						}}
						locale={{
							emptyText: appliedTripSearch
								? 'No trips match this Trip ID.'
								: 'No trips found for the selected filters.',
						}}
					/>

					<Flex justifyContent="space-between" alignItems="center" mt={3}>
						<Box>
							<Text style={{ color: '#1e3a5f', fontWeight: 500 }}>
								Selected : {selectedTrips.length}
							</Text>
							{hiddenSelectedCount > 0 && (
								<HintText>
									{hiddenSelectedCount} selected trip
									{hiddenSelectedCount > 1 ? 's are' : ' is'} not in the current
									search results — they stay selected.
								</HintText>
							)}
						</Box>
						<PrimaryButton onClick={handleProceed} disabled={!selectedTrips.length}>
							Proceed to Invoice Generation
						</PrimaryButton>
					</Flex>
				</Card>
			)}
		</Box>
	);
}

TripSelectionStep.propTypes = {
	clients: PropTypes.array,
	projects: PropTypes.array,
	vehicleTypes: PropTypes.array,
	vehicleNumbers: PropTypes.array,
	onFetchTrips: PropTypes.func.isRequired,
	/** async (filters, file) => { rows, bulkFetch } — must throw on failure so
	 *  the upload modal can show the reason inline. */
	onBulkFetchTrips: PropTypes.func.isRequired,
	onProceed: PropTypes.func.isRequired,
	onSearchVehicleNumbers: PropTypes.func,
	vehicleSearchLoading: PropTypes.bool,
};

TripSelectionStep.defaultProps = {
	clients: [],
	projects: [],
	vehicleTypes: [],
	vehicleNumbers: [],
	onSearchVehicleNumbers: undefined,
	vehicleSearchLoading: false,
};

export default TripSelectionStep;
